import { Box, Tab } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { TabBar } from '@tix-ticketing/ui-components';
import { ClockIcon, DiscountTicketsIcon, GiftcardIcon, MembershipIcon, ReceiptIcon, RotateClockwiseIcon, SettingsIcon, TicketIcon } from '@tix-ticketing/ui-icons/outline';
import { useRouter } from 'next/router';
import { FormattedMessage } from 'react-intl';
import { getApiProfileGiftcardsOptions, getApiProfileMembershipsOptions, getApiProfileOrdersOptions, getApiSpecialOffersOptions } from 'api/generated/@tanstack/react-query.gen';
import { LocaleLink } from 'components/common/locale-link';
import { isVisibleRenewal, isVisibleReservation } from 'lib/helpers/order-helpers';
import { useAuthSession } from 'lib/hooks/use-auth-session';
import { staletime } from 'lib/tanstack-query-client';
export function useNavbarLinks() {
  // Gated on a confirmed session rather than on the mere presence of a token,
  // so these four don't fire (and 401) against one that has already expired.
  const {
    isAuthenticated
  } = useAuthSession();
  const {
    data
  } = useQuery({
    ...getApiProfileGiftcardsOptions(),
    enabled: isAuthenticated
  });
  const {
    data: membershipData
  } = useQuery({
    ...getApiProfileMembershipsOptions(),
    enabled: isAuthenticated
  });
  const {
    data: specialOffersData
  } = useQuery({
    ...getApiSpecialOffersOptions(),
    enabled: isAuthenticated
  });

  // This runs on every signed-in page, not just the account ones, purely to
  // decide whether the Reservations and Renewals links are shown. Reserving and
  // cancelling both invalidate the query, so freshness comes from those rather
  // than from refetching the whole order history on each navigation.
  const {
    data: orderData
  } = useQuery({
    ...getApiProfileOrdersOptions(),
    enabled: isAuthenticated,
    staleTime: staletime.MEDIUM
  });
  const hasGiftcards = (data?.giftCards?.length ?? 0) > 0;
  const hasMemberships = (membershipData?.memberships?.length ?? 0) > 0;
  const hasSpecialOffers = (specialOffersData?.length ?? 0) > 0;
  const hasReservations = !!orderData?.orders?.some(isVisibleReservation);
  const hasRenewals = !!orderData?.orders?.some(isVisibleRenewal);
  return [{
    title: <FormattedMessage id="account.nav.events" defaultMessage={[{
      "type": 0,
      "value": "Events"
    }]} />,
    href: '/account',
    icon: <TicketIcon sx={{
      width: 20,
      mr: 1
    }} />
  }, ...(hasSpecialOffers ? [{
    title: <FormattedMessage id="account.nav.offers" defaultMessage={[{
      "type": 0,
      "value": "Offers"
    }]} />,
    href: '/account/offers',
    icon: <DiscountTicketsIcon sx={{
      width: 20,
      mr: 1
    }} />
  }] : []), ...(hasReservations ? [{
    title: <FormattedMessage id="account.nav.reservations" defaultMessage={[{
      "type": 0,
      "value": "Reservations"
    }]} />,
    href: '/account/reservations',
    icon: <ClockIcon sx={{
      width: 20,
      mr: 1
    }} />
  }] : []), ...(hasRenewals ? [{
    title: <FormattedMessage id="account.nav.renewals" defaultMessage={[{
      "type": 0,
      "value": "Renewals"
    }]} />,
    href: '/account/renewals',
    icon: <RotateClockwiseIcon sx={{
      width: 20,
      mr: 1
    }} />
  }] : []), ...(hasMemberships ? [{
    title: <FormattedMessage id="account.nav.memberships" defaultMessage={[{
      "type": 0,
      "value": "Memberships"
    }]} />,
    href: '/account/memberships',
    icon: <MembershipIcon sx={{
      width: 20,
      mr: 1
    }} />
  }] : []), ...(hasGiftcards ? [{
    title: <FormattedMessage id="account.nav.giftcards" defaultMessage={[{
      "type": 0,
      "value": "Giftcards"
    }]} />,
    href: '/account/giftcards',
    icon: <GiftcardIcon sx={{
      width: 20,
      mr: 1
    }} />
  }] : []), {
    title: <FormattedMessage id="account.nav.orderHistory" defaultMessage={[{
      "type": 0,
      "value": "Order history"
    }]} />,
    href: '/account/orders',
    icon: <ReceiptIcon sx={{
      width: 20,
      mr: 1
    }} />
  }, {
    title: <FormattedMessage id="account.nav.settings" defaultMessage={[{
      "type": 0,
      "value": "Settings"
    }]} />,
    href: '/account/settings',
    icon: <SettingsIcon sx={{
      width: 20,
      mr: 1
    }} />
  }];
}
export function AccountHeader() {
  const {
    pathname
  } = useRouter();
  const {
    isAuthenticated
  } = useAuthSession();
  const navbarLinks = useNavbarLinks();
  if (!isAuthenticated) return null;
  const selected = navbarLinks.find(link => link.href === pathname);
  return <Box sx={{
    my: -4
  }}>
      <TabBar size="large" value={selected?.href ?? false}>
        {navbarLinks.map(link => <Tab key={link.href} value={link.href} label={link.title} component={LocaleLink} href={link.href}
      // LocaleLink inlines `color: inherit`, which would beat the tab
      // bar's selected/unselected colors.
      style={{
        color: undefined
      }} />)}
      </TabBar>
    </Box>;
}